import { Modal, Table, Tag, Button, Space, notification } from "antd"
import { FC, useState } from "react"
import { ContestPeriod, ContestPeriodGame, deleteContestPeriodGame } from "../../../api"
import { defaultFormat } from "utils/date"
import { Column } from "abstract/types/column.type"
import { TrophyOutlined, EditOutlined, DeleteOutlined, PlusOutlined } from "@ant-design/icons"
import { showDeleteConfirm } from "components"
import { GameModal } from "./GameModal"
import { useStore } from "stores/utils/use-store"
import { ContestConfig } from "domains/managingJson/store/types"

interface GamesModalProps {
  visible: boolean;
  onClose: () => void;
  selectedPeriod: ContestPeriod | null;
  games: ContestPeriodGame[];
  loading: boolean;
  onShowChallenges: (game: ContestPeriodGame) => void;
  onRefreshGames: () => void;
  singleRaffleId?: string;
}

export const GamesModal: FC<GamesModalProps> = ({
  visible,
  onClose,
  selectedPeriod,
  games,
  loading,
  onShowChallenges,
  onRefreshGames,
  singleRaffleId,
}) => {
  const [isGameModalVisible, setIsGameModalVisible] = useState(false)
  const [gameModalType, setGameModalType] = useState<"create" | "edit">("create")
  const [editingGame, setEditingGame] = useState<ContestPeriodGame | null>(null)

  const { dataStore: { managingJsonStore: { selectedElement } } } = useStore()
  const contestData = selectedElement.data as ContestConfig
  const isMappedMode = contestData.isMappedRaffleMode || false

  const handleCreateGame = () => {
    setGameModalType("create")
    setEditingGame(null)
    setIsGameModalVisible(true)
  }

  const handleEditGame = (game: ContestPeriodGame) => {
    setGameModalType("edit")
    setEditingGame(game)
    setIsGameModalVisible(true)
  }

  const handleDeleteGame = async(game: ContestPeriodGame) => {
    try {
      await deleteContestPeriodGame(game.contestPeriodId, game.gameId)
      notification.success({ message: "Game deleted successfully" })
      onRefreshGames()
    } catch (error) {
      console.error("Failed to delete game:", error)
      notification.error({ message: "Failed to delete game" })
    }
  }

  const onCloseGameModal = () => {
    setIsGameModalVisible(false)
    setEditingGame(null)
  }

  const baseColumns: Column[] = [
    {
      title: "Game ID",
      dataIndex: "gameId",
      key: "gameId",
      width: 100,
    },
    {
      title: "Created At",
      dataIndex: "createdAt",
      key: "createdAt",
      render: (createdAt: string) => (
        <Tag color="blue">{defaultFormat(new Date(createdAt))}</Tag>
      ),
      width: 200,
    },
    {
      title: "Prizes Count",
      dataIndex: "prizesCount",
      key: "prizesCount",
      width: 120,
    },
    {
      title: "Prize Buff Points",
      dataIndex: "prizeBuffPoints",
      key: "prizeBuffPoints",
      width: 150,
    },
    {
      title: "Prize Gift Card",
      dataIndex: "prizeGiftCard",
      key: "prizeGiftCard",
      render: (prizeGiftCard: string | null) => (
        <div style={{ fontFamily: "monospace", fontSize: "12px" }}>
          {prizeGiftCard || "N/A"}
        </div>
      ),
      width: 200,
    },
  ]

  const raffleColumn: Column = {
    title: "Raffle ID",
    dataIndex: "raffleId",
    key: "raffleId",
    render: (raffleId: string | null) => (
      <div style={{ fontFamily: "monospace", fontSize: "12px" }}>
        {raffleId || "N/A"}
      </div>
    ),
    width: 150,
  }

  const actionsColumn: Column = {
    title: "Actions",
    key: "actions",
    dataIndex: "actions",
    render: (_, game: ContestPeriodGame) => (
      <Space>
        <Button
          type="primary"
          size="small"
          icon={<EditOutlined />}
          onClick={() => handleEditGame(game)}
        >
          Edit
        </Button>
        <Button
          type="default"
          size="small"
          icon={<TrophyOutlined />}
          onClick={() => onShowChallenges(game)}
        >
          Challenges
        </Button>
        <Button
          type="primary"
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => {
            showDeleteConfirm(`game: ${game.gameId}`, undefined, () => handleDeleteGame(game))
          }}
        >
          Delete
        </Button>
      </Space>
    ),
    width: 250,
  }

  const gamesColumns: Column[] = [
    ...baseColumns,
    ...(isMappedMode ? [raffleColumn] : []),
    actionsColumn,
  ]

  return (
    <>
      <Modal
        visible={visible}
        title={
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Games for Period: {selectedPeriod?.id}</span>
            <Button
              style={{ marginRight: 24 }}
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateGame}
            >
              Create Game
            </Button>
          </div>
        }
        width="1100px"
        footer={null}
        onCancel={onClose}
      >
        <Table
          dataSource={games}
          columns={gamesColumns as any}
          rowKey={record => `${record.gameId}-${record.createdAt}`}
          loading={loading}
          pagination={false}
        />
      </Modal>

      <GameModal
        visible={isGameModalVisible}
        onClose={onCloseGameModal}
        editType={gameModalType}
        contestPeriodId={selectedPeriod?.id || ""}
        editingGame={editingGame}
        loading={loading}
        onSuccess={onRefreshGames}
        singleRaffleId={singleRaffleId}
        isSingleMode={!isMappedMode}
      />
    </>
  )
}
